import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarChart3, ChevronRight } from 'lucide-react';

import { env } from '@/env';
import { useGetMyLaddersQuery } from '@/services/qduelsApi';
import { getMyLogtoSub } from '@/lib/logto';
import { TournamentLadder } from './TournamentLadder';
import { useSeasonsQuery } from '@/services/endpoints/leaderboard';
import { SectionCard } from '@/components/hub/SectionCard';
import { EmptyState } from '@/components/common/EmptyState';
import { useGameSelection } from '@/hooks/useGameSelection';
import { PanelWaiting } from '@/components/hub/PanelNotice';
import { listVerdict } from '@/lib/listState';
import { useStalled, useEverFailed, NO_ANSWER_MS } from '@/hooks/useStalled';
import { cdnImage } from '@/lib/cdnImage';
import { cn } from '@/lib/cn';

/**
 * Leaderboard section.
 *
 * WHY THIS STILL RENDERS THE LEGACY LARAVEL SOURCE.
 *
 * ⚠️ Corrected 2026-07-26. This comment used to claim a QDuels-fed leaderboard
 * was impossible, citing a `guildId` gate at `tournament.service.ts:2081`. That
 * gate was REMOVED on 2026-07-20: a tournament with no guildId now credits a
 * GLOBAL ladder (`tournament.service.ts:2465-2500` → `GLOBAL_PLATFORM` /
 * `GLOBAL_GUILD_ID`, `leaderboard.service.ts:28-29`), and the league is already
 * created flat (`tierNames: ['Overall']`). So the path exists in code and
 * `GET /v1/leaderboard?platform=global&guildId=global` is the right call.
 *
 * VERIFICATO IN PRODUZIONE il 2026-07-26 (OVH): la catena **funziona già**.
 * `QBANK_LEADERBOARD_REFERENCE_COIN_ID` è configurata (coin "QLASH Ladder XP"
 * / QLXP, model=1), gli scope M2M funzionano (3 leghe create, 4 iscritti), e la
 * lega Telegram di luglio è ACTIVE con punteggi reali (100, 60). Le due
 * "blockers di runtime" che questo commento citava erano dedotte dai default di
 * `.env.example`, non dall'ambiente vivo: erano false.
 *
 * Restano DUE ragioni vere per non sostituire ancora la sorgente legacy:
 *  1. **Nessun nome.** Le righe portano solo il `sub` Logto
 *     (`leaderboard.service.ts:335` → `{sub, rank, score}`). Una classifica di
 *     UUID non è un prodotto; i nomi vanno risolti via QSocials.
 *  2. **La ladder GLOBALE è ancora vuota.** I punteggi esistenti stanno in una
 *     lega *per-server* (`platform:'telegram'`, guildId del gruppo). La TMA non
 *     ha guildId e leggerebbe `platform=global`, che non ha ancora una lega
 *     perché nessun torneo server-less è stato finalizzato. Non è un blocco di
 *     configurazione: si popola da sé al primo torneo senza guildId.
 *
 * Note also that the TMA does not CREATE tournaments: the ones a user sees in
 * Community are created by the Telegram bot, so they carry a guildId and credit
 * their own per-server ladder. Per-server and global ladders coexist by design.
 */
export function LeaderboardPanel() {
  const { t } = useTranslation('leaderboard');
  const { activeGame } = useGameSelection();

  const { data, isLoading, isError } = useSeasonsQuery(
    { games: activeGame ? String(activeGame.id) : undefined, is_active: true },
    { skip: !env.featureLeaderboardEnabled },
  );
  const seasons = data?.data ?? [];

  // Le ladder tornei del giocatore (per-server e/o globale: coesistono).
  // `sub` è obbligatorio e il server lascia leggere solo il PROPRIO, quindi è
  // una self-query. Senza sub o senza flag lato server la lista resta vuota e
  // il pannello mostra solo la sorgente legacy.
  const mySub = getMyLogtoSub();
  const laddersQ = useGetMyLaddersQuery(mySub ?? '', { skip: !mySub });
  const ladders = laddersQ.data ?? [];

  // ⚠️ Le ladder qduels NON entrano nel verdetto: sono un blocco ADDITIVO che si
  // auto-nasconde quando e' vuoto, quindi un loro 401 non deve oscurare le stagioni. Il
  // verdetto parla della sorgente che governa questa schermata — le stagioni Laravel.
  const seasonsAnswered = data !== undefined;
  const seasonsFailed = useEverFailed(isError, seasonsAnswered);
  const seasonsPending = !seasonsAnswered && !seasonsFailed;
  const seasonsStalled = useStalled(seasonsPending);
  const seasonsNoAnswer = useStalled(seasonsPending, NO_ANSWER_MS);
  const verdict = listVerdict(seasons.length, [
    {
      reachable: true,
      loading: isLoading,
      slow: seasonsStalled,
      failed: seasonsFailed || seasonsNoAnswer,
      answered: seasonsAnswered,
    },
  ]);

  if (!env.featureLeaderboardEnabled) return null;
  if (verdict === 'loading' || verdict === 'slow') {
    return (
      <div className="flex h-32 items-center justify-center">
        <PanelWaiting slow={verdict === 'slow'} />
      </div>
    );
  }
  // 🔴 Misurato su `stg-tma` il 2026-09-06 (or45): con Laravel a 503 questa scheda restava
  // MUTA per ~15 secondi prima di dire «Impossibile caricare le stagioni». I tre stati
  // c'erano gia' — spinner, errore, vuoto — ma erano dedotti da `isLoading` e `isError`, e
  // fra un tentativo e l'altro nessuno dei due e' vero: si poteva cadere sul ramo del vuoto,
  // che AFFERMA. Ora il verdetto e' totale, e la scadenza accorcia l'attesa muta.
  if (verdict !== 'rows') {
    // ⛔ QUI RESTA `load_error`, NON `<PanelNotice>`, e la ragione e' una correzione presa
    // il 2026-09-06 prima del rilascio: la frase che questa scheda gia' produceva DICE LA
    // CAUSA («Impossibile caricare le stagioni»), e sostituirla con un «non lo so» generico
    // — solo perche' arriva prima — sarebbe stato un PEGGIORAMENTO travestito da
    // uniformita'. La scadenza cambia QUANDO la frase compare, mai QUALE frase e'.
    // 📌 *Anticipare un messaggio e' un miglioramento; impoverirlo non lo e'.*
    return verdict === 'empty' ? (
      // Nessuna stagione, e il server lo ha DETTO: illustrazione senza card. La superficie
      // serve a proteggere la lettura di un elenco, e qui l'elenco non c'è.
      <EmptyState>{t('empty')}</EmptyState>
    ) : (
      <p className="card-edge rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
        {t('load_error')}
      </p>
    );
  }

  return (
    // Card-finestra come le altre sezioni: la superficie resta ferma e scorre
    // solo il contenuto. Il pannello e' dichiarato `ownScroll` in HubSections,
    // quindi lo scroller e' questo interno e non quello del contenitore.
    <SectionCard flush className="flex min-h-0 flex-col overflow-hidden">
      <div className="flex min-h-0 flex-col gap-4 overflow-y-auto overscroll-contain p-3 [touch-action:pan-y]">
      {/* Le ladder QDuels dei tornei, se il giocatore ne ha. Vengono PRIMA della
          sorgente legacy: sono la classifica dei nostri tornei, con i nomi veri.
          Ogni ladder si auto-nasconde se vuota, quindi qui non compare nulla
          finché non c'è davvero qualcosa da mostrare. */}
      {ladders.map((l) => (
        <TournamentLadder key={`${l.platform}:${l.guildId}:${l.seasonMonth}`} league={l} />
      ))}

    <ul className="flex flex-col gap-2">
      {seasons.map((season) => (
        <li key={season.id}>
          <Link
            to={`/leaderboards/${season.id}`}
            className={cn(
              'flex items-center gap-3 rounded-2xl bg-bg-secondary p-3',
              'transition-opacity active:opacity-80',
            )}
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-bg-tertiary">
              {season.image ? (
                <img
                  src={cdnImage(season.image)}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              ) : (
                <BarChart3 aria-hidden className="h-6 w-6 text-brand" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-fg">{season.title}</p>
              {season.description && (
                <p className="line-clamp-1 text-xs text-fg-muted">{season.description}</p>
              )}
            </div>
            <ChevronRight aria-hidden className="h-5 w-5 text-fg-muted" />
          </Link>
        </li>
      ))}
    </ul>
      </div>
    </SectionCard>
  );
}
